import MultiplayerCapability from './multiplayer';
import type Instance from '../../instance';
import { Specs } from '@doxyme/extensions-common';

type Constraints = Specs.MultiplayerRoom.Constraints;
type Message = Specs.Multiplayer.ParticipantMessage;
type Participant = Specs.Multiplayer.Participant;
type ParticipantId = Specs.Multiplayer.ParticipantId;

const { Events, Actions, id } = Specs.MultiplayerRoom;

/**
 * Allows for communication between patients and providers within the same room
 */
export default class MultiplayerRoomCapability extends MultiplayerCapability<Constraints> {
  static readonly id = id;

  /**
   * @param role - The role as defined in the host application context - Role.Patient | Role.Provider
   */
  constructor(role: Specs.ReceiveContext.Role, realtimeId: ParticipantId) {
    super(Specs.MultiplayerRoom, role, realtimeId);

    this.handlers.push(
      [Actions.SendAction, this.sendAction.bind(this)],
      [Actions.SendActionAll, this.sendActionAll.bind(this)],
      [Actions.SendMessage, this.sendMessage.bind(this)],
      [Actions.SendMessageAll, this.sendMessageAll.bind(this)],
      [Actions.RequestParticipants, this.requestParticipants.bind(this)],
      [Actions.RequestRealtimeId, this.requestRealtimeId.bind(this)],
    );
  }

  protected override notifyJoins(
    instance: Instance<Constraints>,
    joins: Participant[],
  ): void {
    instance.notify([Events.Joined, joins]);
  }

  protected override notifyDeparts(
    instance: Instance<Constraints>,
    departs: Participant[],
  ): void {
    instance.notify([Events.Departed, departs]);
  }

  protected override notifyParticipants(
    instance: Instance<Constraints>,
    participants: Participant[],
  ): void {
    instance.notify([Events.ParticipantList, participants]);
  }

  protected override notifyMessage(
    instance: Instance<Constraints>,
    message: Message,
  ): void {
    instance.notify([Events.Message, message]);
  }

  protected override notifyRealtimeId(
    instance: Instance<Constraints>,
    realtimeId: ParticipantId,
  ): void {
    instance.notify([Events.RealtimeId, realtimeId]);
  }
}
